import React from 'react';
import { ArrowUpRight, Sparkles, Bot, Wand2, Layers, Network, BookOpen } from 'lucide-react';
import { Reveal, WordReveal } from '../components/Motion';
import { labs, aiTools } from '../mock/data';

const aiCapabilities = [
  { id: 'cap1', ico: Wand2, title: 'Generative UI', desc: 'Prompt-driven Figma + v0 + Galileo workflows that go from intent to credible screens in minutes.' },
  { id: 'cap2', ico: BookOpen, title: 'AI Research Synthesis', desc: 'UX Pilot, Dovetail AI and Maze AI for interview synthesis, theme clustering, and insight reports.' },
  { id: 'cap3', ico: Layers, title: 'Design Systems × AI', desc: 'Token-aware generators that respect your component library — not just produce another generic UI kit.' },
  { id: 'cap4', ico: Network, title: 'Design Engineering', desc: 'Bridging Figma to production with v0 and Cursor — components arrive in code, tokens intact.' },
  { id: 'cap5', ico: Bot, title: 'Agentic UX patterns', desc: 'Designing interfaces for systems that think, defer and act — humans-in-the-loop done right.' },
  { id: 'cap6', ico: Sparkles, title: 'AI Copywriting', desc: 'ChatGPT and Claude as UX writing co-pilots — microcopy, empty states, and stakeholder narratives.' },
];

const Hero = () => (
  <section className="min-h-[90vh] flex flex-col justify-end px-6 md:px-12 lg:px-20 pb-20 pt-32 relative overflow-hidden">
    <div className="blob" style={{ width: 700, height: 700, background: 'var(--accent)', top: '-25%', right: '-10%', opacity: 0.18 }}/>
    <div className="blob" style={{ width: 500, height: 500, background: '#7E6BFF', bottom: '-20%', left: '-10%', opacity: 0.18 }}/>
    <div className="max-w-[1500px] mx-auto w-full relative">
      <Reveal><span className="section-eyebrow">AI Experience Lab</span></Reveal>
      <h1 className="h-display mt-10 text-[14vw] md:text-[10vw]">
        <WordReveal text="Designing"/><br/>
        <span className="font-serif-i text-accent">the unfamiliar.</span>
      </h1>
      <div className="mt-12 grid md:grid-cols-12 gap-10">
        <Reveal delay={0.3} className="md:col-span-7">
          <p className="text-[17px] leading-[1.7] text-muted max-w-2xl">
            A public notebook — experiments where I pair design craft with generative tools, agents and emerging interfaces. Many of these tools quietly power my day-to-day at the studio.
          </p>
        </Reveal>
        <Reveal delay={0.4} className="md:col-span-5 flex md:justify-end items-end gap-3">
          <div className="glass rounded-full px-5 py-3 text-[12px] uppercase tracking-widest flex items-center gap-3">
            <span className="inline-block w-2 h-2 rounded-full bg-accent animate-pulse"/>
            16 tools in active rotation
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Capabilities = () => (
  <section className="px-6 md:px-12 lg:px-20 py-32" style={{ background: 'var(--bg-2)' }}>
    <div className="max-w-[1500px] mx-auto">
      <span className="section-eyebrow">Practice areas</span>
      <h2 className="h-display mt-6 text-5xl md:text-7xl">Where <span className="font-serif-i text-accent">design</span> meets <span className="font-serif-i text-accent">intelligence.</span></h2>
      <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {aiCapabilities.map((c, i) => (
          <Reveal key={c.id} delay={i * 0.05}>
            <div className="card-prem p-8 h-full">
              <c.ico size={26} className="text-accent"/>
              <h3 className="font-display text-2xl mt-8">{c.title}</h3>
              <p className="text-muted text-[13.5px] mt-3 leading-relaxed">{c.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ToolStack = () => (
  <section className="px-6 md:px-12 lg:px-20 py-32">
    <div className="max-w-[1500px] mx-auto">
      <span className="section-eyebrow">The stack</span>
      <h2 className="h-display mt-6 text-5xl md:text-7xl">My AI <span className="font-serif-i text-accent">toolkit.</span></h2>
      <p className="mt-8 max-w-2xl text-muted">The tools currently in rotation for design, research and prototyping. The list evolves &mdash; the discipline does not.</p>
      <div className="mt-14 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {aiTools.map((t, i) => (
          <Reveal key={t.id} delay={i * 0.03}>
            <div className="card-prem p-6 h-full">
              <div className="text-dim font-display text-xs">{String(i+1).padStart(2,'0')}</div>
              <div className="font-display text-lg mt-3">{t.name}</div>
              <div className="text-muted text-[12.5px] mt-2">{t.use}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Workbench = () => (
  <section className="px-6 md:px-12 lg:px-20 py-32" style={{ background: 'var(--bg-2)' }}>
    <div className="max-w-[1500px] mx-auto">
      <span className="section-eyebrow">Current experiments</span>
      <h2 className="h-display mt-6 text-5xl md:text-7xl">The <span className="font-serif-i text-accent">workbench.</span></h2>
      <div className="mt-20 flex flex-col gap-24">
        {labs.map((l, i) => (
          <Reveal key={l.id} delay={i * 0.06}>
            <div className={`grid md:grid-cols-12 gap-10 items-center ${i % 2 === 1 ? 'md:[&>*:first-child]:order-2' : ''}`}>
              <div className="md:col-span-7">
                <div className="relative aspect-[16/10] overflow-hidden rounded-2xl ring-glow" data-cursor="hover">
                  <img src={l.image} alt={l.title} className="w-full h-full object-cover hover:scale-110 transition-transform duration-[1400ms]"/>
                </div>
              </div>
              <div className="md:col-span-5">
                <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest">
                  <span className="text-accent">{l.tag}</span>
                  <span className="text-dim">EXP-0{i+1}</span>
                </div>
                <h3 className="h-display text-3xl md:text-5xl mt-6">{l.title}</h3>
                <p className="text-muted mt-6 leading-relaxed">{l.body}</p>
                <a href={l.notebookUrl} target="_blank" rel="noreferrer" className="link-arrow mt-8">Read the notebook <span className="arrow"><ArrowUpRight size={14}/></span></a>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Hypothesis = () => (
  <section className="px-6 md:px-12 lg:px-20 py-32">
    <div className="max-w-[1500px] mx-auto text-center">
      <span className="section-eyebrow justify-center" style={{ display: 'inline-flex' }}>Hypothesis</span>
      <h2 className="h-display mt-10 text-4xl md:text-6xl max-w-4xl mx-auto leading-[1.05]">
        AI won&apos;t replace designers. <span className="font-serif-i text-accent">Designers using AI</span> will replace designers who don&apos;t.
      </h2>
    </div>
  </section>
);

const AI = () => (
  <div className="relative">
    <Hero/>
    <Capabilities/>
    <ToolStack/>
    <Workbench/>
    <Hypothesis/>
  </div>
);

export default AI;
